import React, { useState, useEffect } from 'react'
import {
  Card,
  Input,
  Button,
  Space,
  Typography,
  Tag,
  Tooltip,
  AutoComplete,
  message,
  Progress
} from 'antd'
import { 
  SearchOutlined, 
  RobotOutlined, 
  BulbOutlined,
  ThunderboltOutlined,
  HistoryOutlined
} from '@ant-design/icons'
import { apiService } from '../services/api'
import { QueryRequest, QuerySuggestion, QueryTemplate } from '../types/api'

const { TextArea } = Input
const { Title, Text } = Typography

interface QueryInterfaceProps {
  onQuery: (request: QueryRequest) => void
  loading: boolean
  progress?: number
}

const QueryInterface: React.FC<QueryInterfaceProps> = ({ onQuery, loading, progress = 0 }) => {
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<QuerySuggestion | null>(null)
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<any[]>([])
  const [queryHistory, setQueryHistory] = useState<string[]>([])

  useEffect(() => {
    loadSuggestions()
    loadQueryHistory()
  }, [])

  const loadSuggestions = async () => {
    try {
      const data = await apiService.getQuerySuggestions()
      setSuggestions(data)
    } catch (error) {
      console.error('获取查询建议失败:', error)
    }
  }

  const loadQueryHistory = () => {
    const history = localStorage.getItem('query_history')
    if (history) {
      setQueryHistory(JSON.parse(history))
    }
  }

  const saveQueryToHistory = (queryText: string) => {
    const newHistory = [queryText, ...queryHistory.filter(q => q !== queryText)].slice(0, 10)
    setQueryHistory(newHistory)
    localStorage.setItem('query_history', JSON.stringify(newHistory))
  }

  const handleSearch = async (value: string) => {
    if (!value.trim()) return
    
    try {
      const data = await apiService.getQuerySuggestions(value)
      const options = data.suggestions.map((suggestion) => ({
        value: suggestion,
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BulbOutlined style={{ marginRight: 8, color: '#faad14' }} />
            {suggestion}
          </div>
        )
      }))
      setAutoCompleteOptions(options)
    } catch (error) {
      console.error('获取自动完成建议失败:', error)
    }
  }

  const handleSubmit = () => {
    if (!query.trim()) {
      message.warning('请输入查询内容')
      return
    }

    saveQueryToHistory(query.trim())
    onQuery({
      query: query.trim(),
      include_explanation: true
    })
  }

  const handleTemplateClick = (template: QueryTemplate) => {
    setQuery(template.example)
  }

  const handleHistoryClick = (historyQuery: string) => {
    setQuery(historyQuery)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit()
    }
  }

  return (
    <Card className="query-card" style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 8 }}>
          <RobotOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          AI 智能查询
        </Title>
        <Text type="secondary">
          使用自然语言描述您的查询需求，AI 将自动生成 SQL 并执行查询
        </Text>
      </div>

      <div style={{ marginBottom: 16 }}>
        <AutoComplete
          style={{ width: '100%' }}
          options={autoCompleteOptions}
          onSearch={handleSearch}
          onSelect={(value) => setQuery(value)}
          value={query}
          onChange={setQuery}
        >
          <TextArea
            placeholder="例如：查询太元工厂的鸡排成本、对比三个工厂的平均利润、显示2024年1月的所有产品..."
            rows={3}
            className="query-input"
            onKeyDown={handleKeyPress}
          />
        </AutoComplete>
        
        <div style={{ 
          marginTop: 8, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            提示：按 Ctrl+Enter 快速提交查询
          </Text>
          
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSubmit}
            loading={loading}
            className="query-button"
          >
            {loading ? '查询中...' : '开始查询'}
          </Button>
        </div>

        {/* Progress indicator for AI processing */}
        {loading && progress > 0 && (
          <div style={{ marginTop: 16 }}>
            <Progress
              percent={progress}
              status={progress === 100 ? 'success' : 'active'}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              format={(percent) => `AI处理中 ${percent}%`}
            />
            <Text type="secondary" style={{ fontSize: '12px', marginTop: 4, display: 'block' }}>
              {progress < 30 && '正在连接AI服务...'}
              {progress >= 30 && progress < 60 && '正在分析查询内容...'}
              {progress >= 60 && progress < 90 && '正在生成SQL查询...'}
              {progress >= 90 && progress < 100 && '正在执行查询...'}
              {progress === 100 && '查询完成！'}
            </Text>
          </div>
        )}
      </div>

      {/* 查询模板 */}
      {suggestions?.templates && suggestions.templates.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <ThunderboltOutlined style={{ marginRight: 4, color: '#faad14' }} />
            <Text strong>快速模板</Text>
          </div>
          <Space wrap>
            {suggestions.templates.map((template, index) => (
              <Tooltip key={index} title={template.template}>
                <Tag 
                  color="blue" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleTemplateClick(template)}
                >
                  {template.name}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        </div>
      )}

      {/* 查询历史 */}
      {queryHistory.length > 0 && (
        <div>
          <div style={{ marginBottom: 8 }}>
            <HistoryOutlined style={{ marginRight: 4, color: '#52c41a' }} />
            <Text strong>查询历史</Text>
          </div>
          <Space wrap>
            {queryHistory.slice(0, 5).map((historyQuery, index) => (
              <Tag 
                key={index}
                color="green" 
                style={{ cursor: 'pointer', maxWidth: 200 }}
                onClick={() => handleHistoryClick(historyQuery)}
              >
                {historyQuery.length > 20 ? `${historyQuery.slice(0, 20)}...` : historyQuery}
              </Tag>
            ))}
          </Space>
        </div>
      )}
    </Card>
  )
}

export default QueryInterface
